import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import type { LessonData } from '../data/subject-teacher-mock'

interface LessonSheetState {
  newLessonOpen: boolean
  viewLessonOpen: boolean
  viewLesson: LessonData | null
  initialJournalId: number | null
  openNewLesson: (journalId?: number | null) => void
  openViewLesson: (lesson: LessonData) => void
  closeNewLesson: () => void
  closeViewLesson: () => void
  editFromView: () => void
  duplicateFromView: () => void
}

const LessonSheetCtx = createContext<LessonSheetState | null>(null)

export function useLessonSheet() {
  const ctx = useContext(LessonSheetCtx)
  if (!ctx) throw new Error('useLessonSheet must be used within LessonSheetProvider')
  return ctx
}

export function LessonSheetProvider({ children }: { children: ReactNode }) {
  const [newLessonOpen, setNewLessonOpen] = useState(false)
  const [viewLessonOpen, setViewLessonOpen] = useState(false)
  const [viewLesson, setViewLesson] = useState<LessonData | null>(null)
  const [initialJournalId, setInitialJournalId] = useState<number | null>(null)

  const openNewLesson = useCallback((journalId?: number | null) => {
    setInitialJournalId(journalId ?? null)
    setNewLessonOpen(true)
  }, [])

  const openViewLesson = useCallback((lesson: LessonData) => {
    setViewLesson(lesson)
    setViewLessonOpen(true)
  }, [])

  const closeNewLesson = useCallback(() => setNewLessonOpen(false), [])
  const closeViewLesson = useCallback(() => setViewLessonOpen(false), [])

  const editFromView = useCallback(() => {
    setViewLessonOpen(false)
    setNewLessonOpen(true)
  }, [])

  const duplicateFromView = useCallback(() => {
    setViewLessonOpen(false)
    setNewLessonOpen(true)
  }, [])

  return (
    <LessonSheetCtx.Provider
      value={{
        newLessonOpen,
        viewLessonOpen,
        viewLesson,
        initialJournalId,
        openNewLesson,
        openViewLesson,
        closeNewLesson,
        closeViewLesson,
        editFromView,
        duplicateFromView,
      }}
    >
      {children}
    </LessonSheetCtx.Provider>
  )
}
